/**
 * Every store write of a run's `reportPointers` goes through this merge, so no path can
 * assign the field from a single response and clobber what another path already merged.
 *
 * The merge is MONOTONIC. Read-through hints are at-least-once and unordered, so a response
 * serialized from an older snapshot must not shrink a list a newer one already grew. It
 * unions and never removes, which is safe because `report_pointers` is append-only
 * server-side.
 */

function canonical(value: unknown): string {
  if (value === null || typeof value !== "object") {
    // `JSON.stringify(undefined)` is undefined; keep the identity a string either way.
    return JSON.stringify(value) ?? String(value);
  }
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(",")}}`;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Dedupe identities are per KIND and mirror the server's own dedupe keys
 * (`server/lib/fanoutPointer.js`).
 *
 * A forecast entry is identified by its `row_key`, while a resolution entry carries no
 * `row_key` and is identified by `(qkey, attempted_at)`. Keying every entry on `row_key`
 * would collapse all resolution entries onto one undefined key and destroy resolution history.
 *
 * Anything else, such as an unrecognised or future `kind` or a known kind missing its key
 * fields, falls back to its canonical serialization. That fallback is for DEDUPING, never for
 * ADMISSION: such an entry is always kept, and the deterministic identity only stops a
 * repeated copy of it from being appended again on every hint.
 */
export function pointerIdentity(pointer: unknown): string {
  if (pointer && typeof pointer === "object" && !Array.isArray(pointer)) {
    const p = pointer as Record<string, unknown>;
    if (p.kind === "forecast" && nonEmptyString(p.row_key)) return `forecast\u0000${p.row_key}`;
    if (p.kind === "resolution" && nonEmptyString(p.qkey) && nonEmptyString(p.attempted_at)) {
      return `resolution\u0000${p.qkey}\u0000${p.attempted_at}`;
    }
  }
  return `other\u0000${canonical(pointer)}`;
}

/**
 * Returns `null` rather than a copy when there is nothing to add, which makes a redelivered
 * hint a true no-op: the caller keeps its own reference, so the store does not churn and
 * nothing re-renders. Entries already in `existing` are never removed or reordered,
 * duplicates included.
 */
export function unionReportPointers(existing: unknown, incoming: unknown): unknown[] | null {
  if (!Array.isArray(incoming) || incoming.length === 0) return null;
  const base = Array.isArray(existing) ? existing : [];
  const seen = new Set(base.map(pointerIdentity));
  const additions: unknown[] = [];
  for (const pointer of incoming) {
    const id = pointerIdentity(pointer);
    if (seen.has(id)) continue;
    seen.add(id);
    additions.push(pointer);
  }
  return additions.length ? [...base, ...additions] : null;
}

export function mergeReportPointers(existing: unknown, incoming: unknown): unknown[] {
  return unionReportPointers(existing, incoming) ?? (Array.isArray(existing) ? existing : []);
}
